import * as THREE from 'three';
import { bronze, canDraw } from './hud-materials.js';
import { boltGeometry, cogGeometry, iron } from './hud-parts.js';
import { unpickable } from './radial-menu.js';

/** How far the clock's works reach from its centre, in CSS pixels at size 1. */
export const CLOCK = 52;
const DIAL = 31;

/**
 * The game clock, which is also the pause button: bare clockwork with no case. A dial painted with the day sky on one
 * half and the night on the other turns once a day under a gilt pointer at the top, so the hour reads as sun or moon and how far
 * they have climbed. Around it the cogs turn while the game runs and stand still while it is paused, when a stop-gem under the dial
 * burns amber.
 */
export class HudClock extends THREE.Group {
  hovered = false; paused = false;
  /** In-game minutes (0–1440). */
  minutes = 720;
  private metal = Object.assign(bronze(53, 0x5d4730), { flatShading: true });
  private brass = new THREE.MeshStandardMaterial({ color: 0xb08a4a, metalness: .85, roughness: .3 });
  private hardware = iron();
  private dialMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: .55, metalness: .1, emissive: 0xffffff, emissiveIntensity: .32 });
  private dial: THREE.Mesh;
  private cogs: { mesh: THREE.Mesh; speed: number }[] = [];
  private gem = new THREE.MeshStandardMaterial({ color: 0x4a2a10, emissive: 0xffa040, emissiveIntensity: 0, roughness: .15, flatShading: true, transparent: true, opacity: .95 });
  private glow: THREE.Sprite | null;
  private angle: number | null = null;
  private run = 1; private lift = 0; private flashing = 0;
  private disposables: { dispose(): void }[] = [];

  constructor(glow: THREE.Texture | null) {
    super();
    this.userData.button = 'pause';
    const keep = <T extends { dispose(): void }>(item: T) => { this.disposables.push(item); return item; };
    const bolt = keep(boltGeometry(3.4));
    // The works, behind and around the dial: a great wheel under it and smaller cogs meshing at its rim, alternate ones turning back.
    const wheel = (radius: number, teeth: number, x: number, y: number, z: number, speed: number, material: THREE.Material) => {
      const mesh = new THREE.Mesh(keep(cogGeometry(radius, teeth, radius * .16, 3, radius * .3)), material); mesh.position.set(x, y, z);
      this.cogs.push({ mesh, speed }); this.add(mesh);
      const hub = new THREE.Mesh(bolt, this.hardware); hub.scale.setScalar(radius * .045); hub.position.set(x, y, z + 4); this.add(hub);
    };
    wheel(DIAL + 6, 36, 0, 0, -14, .18, this.metal);
    wheel(13, 12, -36, -26, -8, -.62, this.brass);
    wheel(10, 10, 37, -27, -9, .8, this.brass);
    wheel(9, 9, 38, 26, -8, -.9, this.metal);
    wheel(11, 11, -37, 27, -9, .7, this.metal);
    // The dial and its gilt bezel; the pointer stands fixed at the top.
    this.dial = new THREE.Mesh(keep(new THREE.CircleGeometry(DIAL, 64)), this.dialMaterial); this.dial.position.z = -1;
    const map = canDraw() ? keep(dialTexture()) : null;
    if (map) Object.assign(this.dialMaterial, { map, emissiveMap: map });
    const bezel = new THREE.Mesh(keep(new THREE.TorusGeometry(DIAL + 1.5, 2.4, 10, 64)), this.brass); bezel.position.z = 0;
    const pointerShape = new THREE.Shape([new THREE.Vector2(-5, 0), new THREE.Vector2(5, 0), new THREE.Vector2(0, -11)]);
    const pointer = new THREE.Mesh(keep(new THREE.ExtrudeGeometry(pointerShape, { depth: 2, bevelEnabled: true, bevelSize: .8, bevelThickness: .8, bevelSegments: 1 })), this.brass);
    pointer.position.set(0, DIAL + 6, 3);
    this.add(this.dial, bezel, pointer);
    // The stop-gem, set on the great wheel's rim under the dial.
    const stone = new THREE.Mesh(keep(new THREE.OctahedronGeometry(5.5, 0).scale(1.3, .9, .7)), this.gem); stone.position.set(0, -(DIAL + 9), 6);
    this.add(stone);
    this.glow = glow ? unpickable(new THREE.Sprite(new THREE.SpriteMaterial({ map: glow, color: 0xffa040, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true, opacity: 0 }))) : null;
    if (this.glow) { this.glow.scale.setScalar(44); this.glow.position.set(0, -(DIAL + 9), 10); this.add(this.glow); keep(this.glow.material); }
    keep(this.metal); keep(this.brass); keep(this.hardware); keep(this.dialMaterial); keep(this.gem);
  }

  flash() { this.flashing = 1; }
  /** A fight has begun: the works lurch and race for a moment, the clock shudders and the stop-gem flares red. */
  jolt() { this.alarm = 1; }
  private alarm = 0;
  private amber = new THREE.Color(0xffa040);
  private red = new THREE.Color(0xff3018);

  update(dt: number, time: number) {
    // The works spin up and run down rather than snapping.
    this.run += ((this.paused ? 0 : 1) - this.run) * Math.min(1, dt * 4);
    this.alarm = Math.max(0, this.alarm - dt * .9);
    for (const cog of this.cogs) cog.mesh.rotation.z += cog.speed * (this.run + this.alarm ** 2 * 9) * dt;
    // Noon puts the sun under the pointer; the dial turns a full circle a day, eased toward the hour so snapshots never jerk it.
    const target = Math.PI - this.minutes / 1440 * Math.PI * 2;
    if (this.angle === null || Math.abs(target - this.angle) > 1) this.angle = target;
    else this.angle += (target - this.angle) * Math.min(1, dt * 6);
    this.dial.rotation.z = this.angle;
    const stopped = 1 - this.run;
    this.gem.emissive.copy(this.amber).lerp(this.red, Math.min(1, this.alarm * 2));
    this.gem.emissiveIntensity = Math.max(stopped * (1.6 + Math.sin(time * 3) * .25), this.alarm * (2.4 + Math.sin(time * 22) * .6));
    this.rotation.z = Math.sin(time * 47) * this.alarm ** 3 * .06;
    if (this.glow) { const glow = this.glow.material as THREE.SpriteMaterial; glow.opacity = Math.max(stopped * .8, this.alarm * .9) + this.flashing * .6; glow.color.copy(this.gem.emissive); }
    this.lift += ((this.hovered ? 1 : 0) - this.lift) * Math.min(1, dt * 14);
    this.flashing = Math.max(0, this.flashing - dt * 3);
    this.position.z = this.lift * 8;
    this.scale.setScalar(this.userData.size * (1 + this.lift * .05 + this.flashing * .06));
  }

  dispose() { for (const item of this.disposables) item.dispose(); this.dialMaterial.map?.dispose(); }
}

/**
 * The dial face: day sky above with the sun at its top (noon), night below with the moon at its bottom (midnight), dawn and dusk
 * glowing where they meet, and a tick for each hour.
 */
function dialTexture() {
  const size = 256, canvas = document.createElement('canvas'); canvas.width = canvas.height = size;
  const g = canvas.getContext('2d')!, c = size / 2, r = size / 2;
  const sky = g.createLinearGradient(0, 0, 0, size);
  sky.addColorStop(0, '#6f9cc4'); sky.addColorStop(.38, '#a9b9b4'); sky.addColorStop(.48, '#d98a46'); sky.addColorStop(.54, '#5a3a4a');
  sky.addColorStop(.64, '#1d2440'); sky.addColorStop(1, '#0b1022');
  g.fillStyle = sky; g.beginPath(); g.arc(c, c, r, 0, Math.PI * 2); g.fill();
  // Stars on the night half.
  let seed = 11; const random = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
  g.fillStyle = '#e8e4d0';
  for (let i = 0; i < 40; i++) { const a = Math.PI * (.08 + random() * .84), d = r * (.25 + random() * .65); g.globalAlpha = .4 + random() * .6; g.fillRect(c + Math.cos(a) * d, c + Math.sin(a) * d, 2, 2); }
  g.globalAlpha = 1;
  // The sun at the top, the moon at the bottom (painted upside down, so it reads upright when midnight turns it to the top).
  const sun = g.createRadialGradient(c, c - r * .66, 2, c, c - r * .66, r * .26);
  sun.addColorStop(0, '#fff3c4'); sun.addColorStop(.45, '#f2c25a'); sun.addColorStop(1, 'rgba(242,170,70,0)');
  g.fillStyle = sun; g.beginPath(); g.arc(c, c - r * .66, r * .26, 0, Math.PI * 2); g.fill();
  g.fillStyle = '#e7e2cc'; g.beginPath(); g.arc(c, c + r * .66, r * .14, 0, Math.PI * 2); g.fill();
  g.fillStyle = '#152038'; g.beginPath(); g.arc(c + r * .06, c + r * .62, r * .12, 0, Math.PI * 2); g.fill();
  // Hour ticks, longer every six hours.
  g.strokeStyle = 'rgba(30,20,10,.75)'; g.lineCap = 'round';
  for (let h = 0; h < 24; h++) {
    const a = h / 24 * Math.PI * 2, long = h % 6 === 0, inner = r * (long ? .8 : .88);
    g.lineWidth = long ? 5 : 3; g.beginPath(); g.moveTo(c + Math.sin(a) * inner, c - Math.cos(a) * inner); g.lineTo(c + Math.sin(a) * r * .97, c - Math.cos(a) * r * .97); g.stroke();
  }
  const texture = new THREE.CanvasTexture(canvas); texture.colorSpace = THREE.SRGBColorSpace; texture.anisotropy = 4;
  return texture;
}
